import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_ICON_DEFAULT_OPTIONS } from '@angular/material/icon';
import { MAT_RADIO_DEFAULT_OPTIONS } from '@angular/material/radio';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  PreloadAllModules,
  provideRouter,
  withInMemoryScrolling,
  withPreloading,
} from '@angular/router';
import { provideTranslateService, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { firstValueFrom, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { routes } from './app.routes';
import { Languages } from './common/enums/languages.enum';
import { surveyInterceptor } from './components/survey.interceptor.functional';
import { LanguageService } from './core/providers/language/language.service';
import { TranslateLoaderFactory } from './core/providers/translations/app.translations';

export const appConfig = {
  providers: [
    provideZoneChangeDetection(),
    // Preload lazy routes after bootstrap so in-session navigations (e.g.
    // campaign -> demographics) resolve their chunks from memory instead of
    // re-fetching a possibly-stale hash mid-flow.
    // Reset to the top on every route change so a new page (e.g. landing on the
    // OMQ after submitting the survey) always starts at the header, never mid-scroll.
    provideRouter(
      routes,
      withPreloading(PreloadAllModules),
      withInMemoryScrolling({ scrollPositionRestoration: 'top' })
    ),
    provideAnimations(),
    provideHttpClient(withInterceptors([surveyInterceptor])),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: TranslateLoaderFactory,
        deps: [HttpClient],
      },
    }),
    provideAppInitializer(() => {
      const translate = inject(TranslateService);
      const language = inject(LanguageService);
      const activeLang = language.getTranslationFileName(language.getInitialLanguage());
      const fallbackLang = language.getTranslationFileName(Languages.britishEnglish);

      return firstValueFrom(
        forkJoin([translate.use(activeLang), translate.setFallbackLang(fallbackLang)]).pipe(
          catchError(() => of(null))
        )
      );
    }),
    {
      provide: MAT_RADIO_DEFAULT_OPTIONS,
      useValue: { color: 'primary' },
    },
    {
      provide: MAT_ICON_DEFAULT_OPTIONS,
      useValue: { fontSet: 'material-icons-round' },
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline', subscriptSizing: 'dynamic' },
    },
    {
      provide: LOCALE_ID,
      deps: [LanguageService],
      useFactory: (languageService: LanguageService) => {
        const lang = languageService.getBrowserLang();
        // np. Languages.americanEnglish to en-US
        return lang === Languages.americanEnglish ? 'en-US' : navigator.language;
      },
    },
  ],
};
